import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, PanelCard, Alert, Icon } from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { vendorApiClient } from '../lib/vendorApiClient';
import { storeTypeLabelKey, STORE_TYPE_OTHER } from '../lib/storeTypes';
import { LOCATION_COUNTS, POS_SYSTEMS } from '../lib/applyOptions';
import ApplyForm from './ApplyForm';

/**
 * §1C "Application under review" (Figma 370:4) — the post-submit status screen
 * shown while an applicant's request is non-terminal. A confirmation hero, a
 * 3-step status pipeline (Submitted ✓ → Under review ● → Decision ○), a
 * what-happens-next note, the submitted summary, and edit / withdraw / support.
 */

const GREEN = '#25862D';
const ORANGE = '#F67A1A';
const NAVY = '#343F5B';
const GREY = '#9aa5b1';
// Ruled 2026-09-17 (§15dd): one vendor support address, support@glplusinc.com.
const SUPPORT_EMAIL = 'support@glplusinc.com';

export default function PendingStatus() {
  const { t, i18n } = useTranslation();
  const { data, refresh } = useVendor();
  const [editing, setEditing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const app = data?.application;
  if (!app) return null;
  if (editing) return <ApplyForm existing={app} onCancel={() => setEditing(false)} />;

  const bi = app.businessInfo ?? {};
  const business = bi.businessName ?? app.businessName ?? '';
  const decided = app.status === 'approved';
  const submitted = new Date(app.createdAt).toLocaleDateString(i18n.language);

  const locKey = LOCATION_COUNTS.find((o) => o.slug === bi.numberOfLocations)?.labelKey;
  const locSuffix = locKey ? ` · ${t(locKey)}` : '';
  const typeKey = bi.businessType === STORE_TYPE_OTHER ? null : storeTypeLabelKey(bi.businessType ?? null);
  const typeLabel = typeKey ? t(typeKey) : bi.businessTypeOther || '—';
  const locationLabel = bi.address ? `${bi.address.city}, ${bi.address.state}${locSuffix}` : '—';
  const posLabel = app.posSystem ? t(POS_SYSTEMS.find((o) => o.slug === app.posSystem)?.labelKey ?? '') || app.posSystem : '—';
  const snapLabel = bi.snapAccepted ? (bi.fnsNumber ? t('pending.snapYes', { fns: bi.fnsNumber }) : t('pending.snapYesNoFns')) : t('pending.snapNo');

  const withdraw = async () => {
    if (!window.confirm(t('pending.withdrawConfirm'))) return;
    setWithdrawing(true);
    setError(null);
    try {
      await vendorApiClient.withdrawApplication(app.applicationId);
      refresh();
    } catch (e) {
      setError((e as Error).message);
      setWithdrawing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
      {/* Hero */}
      <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#E3F4E7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="check" size={30} color={GREEN} ariaLabel="" />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>{t('pending.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('pending.heroSub', { business })}
        </Typography>
      </Box>

      {error && <Alert severity="error">{t('pending.error', { error })}</Alert>}

      {/* Status pipeline */}
      <PanelCard accent={GREEN} title={t('pending.statusTitle')} sx={{ height: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <PipelineRow
            state="complete"
            title={t('pending.stepSubmitted')}
            meta={`${business}${locSuffix}`}
            right={t('pending.submittedComplete', { date: submitted })}
            rightColor={GREEN}
            first
          />
          <PipelineRow
            state={decided ? 'complete' : 'inProgress'}
            title={t('pending.stepUnderReview')}
            meta={t('pending.stepUnderReviewMeta')}
            right={t('pending.underReviewEta')}
            rightColor={ORANGE}
          />
          <PipelineRow
            state={decided ? 'complete' : 'pending'}
            title={t('pending.stepDecision')}
            meta={t('pending.stepDecisionMeta', { email: data?.email ?? '' })}
            right={t('pending.decisionPending')}
            rightColor={GREY}
          />
        </Box>
      </PanelCard>

      {/* What happens next */}
      <Alert severity="info" title={t('pending.whatNextTitle')}>
        {t('pending.whatNext')}
      </Alert>

      {/* What you submitted */}
      <PanelCard accent={NAVY} sx={{ height: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('pending.summaryTitle')}</Typography>
          <LinkButton color={ORANGE} onClick={() => setEditing(true)}>{t('pending.editApplication')}</LinkButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <SummaryRow label={t('pending.sumBusiness')} value={business} />
          <SummaryRow label={t('pending.sumType')} value={typeLabel} />
          <SummaryRow label={t('pending.sumLocation')} value={locationLabel} />
          <SummaryRow label={t('pending.sumSnap')} value={snapLabel} />
          <SummaryRow label={t('pending.sumPos')} value={posLabel} />
        </Box>
      </PanelCard>

      {/* Footer actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <LinkButton color={GREY} onClick={withdraw} disabled={withdrawing}>
          {withdrawing ? t('pending.withdrawing') : t('pending.withdraw')}
        </LinkButton>
        <Typography sx={{ color: GREY }}>·</Typography>
        <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: ORANGE, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
          {t('pending.contactSupport')}
        </Box>
      </Box>
    </Box>
  );
}

type RowState = 'complete' | 'inProgress' | 'pending';

function PipelineRow({ state, title, meta, right, rightColor, first }: { state: RowState; title: string; meta: string; right: string; rightColor: string; first?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, borderTop: first ? 'none' : '1px solid #eef1f4' }}>
      <StepDot state={state} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{meta}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: rightColor, whiteSpace: 'nowrap' }}>{right}</Typography>
    </Box>
  );
}

function StepDot({ state }: { state: RowState }) {
  if (state === 'complete') {
    return (
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="check" size={15} color="white" ariaLabel="" />
      </Box>
    );
  }
  if (state === 'inProgress') {
    return (
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#fff' }} />
      </Box>
    );
  }
  return <Box sx={{ width: 26, height: 26, borderRadius: '50%', border: `2px solid ${GREY}`, flexShrink: 0 }} />;
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography sx={{ width: 160, flexShrink: 0, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, color: '#1f2733' }}>{value}</Typography>
    </Box>
  );
}

function LinkButton({ color, onClick, disabled, children }: { color: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{ border: 'none', background: 'transparent', color, fontWeight: 700, fontSize: 14, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', p: 0, opacity: disabled ? 0.6 : 1 }}
    >
      {children}
    </Box>
  );
}
